/* eslint-disable no-sequences */
// @flow
/* eslint-disable no-param-reassign */
/* eslint-disable arrow-body-style */ import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { hierarchy, treemap, treemapSquarify } from 'd3-hierarchy';
import { t } from 'javascripts/utils/withTranslation';
import useResizeObserver from 'use-resize-observer';
import { AnimatePresence, motion } from 'framer-motion/dist/framer-motion';
import useMediaquery from 'javascripts/hooks/useMediaquery';
import { MEDIA_QUERIES } from 'javascripts/constants';
import type { Data, HierarchyNode, Item, Tree, Wording } from '../types';
import Tile from './Tile';
import RootTile from './RootTile';
import DiagramList from './DiagramList';
import IconLink from '../../../atoms/icon-link/react/IconLink';
import ListSwitch from './ListSwitch';

type Props = {
  data: Item[],
  inputValue?: number,
  variant?: 'default' | 'calculator',
  display: 'diagram' | 'list',
  setDisplay: () => void,
  wording?: Wording,
};

function distributeWithMinTileSize(d: Tree) {
  const minValue = 0.05;
  // Ensure each node has a minimum value of 5%
  if (
    d.data.percentage !== undefined &&
    d.data.percentage < minValue &&
    d.parent
  ) {
    const diff = minValue - d.data.percentage;
    if (d.data.originalPercentage === undefined) {
      d.data.originalPercentage = d.data.percentage; // Save the original value if not already set
    }
    d.data.percentage = minValue;
    d.value = d.parent.value ? d.data.percentage * d.parent.value : 0;

    // Distribute the weight of the changed value proportionally among siblings that are bigger than 5%
    const siblings = d.parent.children.filter(
      (child) =>
        child !== d &&
        child.data.percentage !== undefined &&
        child.data.percentage > minValue
    );
    const totalSiblingPercentage = siblings.reduce(
      (sum, sibling) => sum + (sibling.data.percentage || 0),
      0
    );
    siblings.forEach((sibling) => {
      if (sibling.data.percentage !== undefined) {
        sibling.data.percentage -=
          (sibling.data.percentage / totalSiblingPercentage) * diff;
      }
      sibling.value =
        (sibling.data.percentage || 0) * (sibling.parent?.value || 0);
    });
  } else if (d.parent) {
    if (d.data.originalPercentage === undefined) {
      d.data.originalPercentage = d.data.percentage; // Save the original value if not already set
    }
    d.value = (d.data?.percentage || 0) * (d.parent?.value || 0);
  }
}

const Diagram = ({
  data,
  inputValue,
  variant,
  display,
  wording,
  setDisplay,
}: Props): React$MixedElement => {
  const isTablet = useMediaquery(MEDIA_QUERIES.l);
  const ref = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLElement | null>(null);
  const { width = 1000 } = useResizeObserver<HTMLDivElement>({
    ref: frameRef,
  });
  const presetRootHeight = 310;
  const [rootHeight, setRootHeight] = useState(presetRootHeight);
  const [height, setHeight] = useState(width * 0.9 + rootHeight + 28);
  const rootHierarchy = useMemo(
    () =>
      hierarchy({ title: 'root', children: data })
        .sum((d) => (d.children ? 0 : d.value)) // Ignore values on higher levels
        .sort((a, b) => b.value - a.value),
    [data]
  );
  const [activeTree, setActiveTree] = React.useState<Tree | void>();
  const ratio = 1;
  const treeGenerator = (w: number, h: number, r: number) =>
    treemap<Item>()
      .size([w, h])
      .paddingInner(8)
      .round(true)
      .tile(
        // inspired https://observablehq.com/@d3/stretched-treemap
        // Since squarify.ratio does not imply orientation, this approach wraps the tile function to divide x-values by ratio, and then scales them back after tiling
        (
          (tile: any) =>
          (node: Tree, x0: number, y0: number, x1: number, y1: number) => {
            tile(node, x0 / r, y0, x1 / r, y1);
            node.children.forEach((child) => {
              child.x0 *= r;
              child.x1 *= r;
            });
          }
        )(treemapSquarify.ratio(1))
      );

  useEffect(() => {
    setHeight(width * 0.6);
  }, [width]);

  useEffect(() => {
    // make space for the root tile
    setRootHeight(
      activeTree?.data?.parent || activeTree?.parent ? presetRootHeight : 0
    );
    if (activeTree?.data?.parent || activeTree?.parent) {
      frameRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeTree]);

  useEffect(() => {
    const newRoot = rootHierarchy.copy();
    // calculate the actual value ("partial") on the basis of the absolute value of the leaf nodes in comparison to the input value
    newRoot
      .eachBefore((d) => {
        d.data.parent = d.parent;
        if (d.parent) {
          d.data.absolute = d.value;
          const percentage = d.value / d.parent.value;
          d.data.percentage = percentage;
          d.data.partial = percentage * d.parent.data.partial;
          d.data.id = `${d.depth}d--${d.data.title.replace(/\s/g, '-')}`;
        } else {
          d.data.partial = inputValue;
          d.data.id = 'root';
        }
      })
      .each(distributeWithMinTileSize);

    setActiveTree(treeGenerator(width, height, ratio)(newRoot));
  }, [inputValue, width, height]);

  const handleClick = (leaf: Tree) => {
    if (leaf.children) {
      // set a new tree with the clicked leaf as the root
      const newRoot = hierarchy(leaf.data)
        .sum((d) => (d.children ? 0 : d.value)) // Ignore values on higher levels
        .sort((a, b) => b.value - a.value)
        .each(distributeWithMinTileSize);
      setActiveTree(treeGenerator(width, height, ratio)(newRoot));
    }
  };

  const allShapes = activeTree?.children.map((leaf) => {
    // on sub levels the offset pushes the tiles down by the height of its header + padding
    const yOffset =
      activeTree?.data?.parent || activeTree?.parent ? rootHeight + 8 : 0;
    return (
      <Tile
        key={leaf.data.id}
        tile={leaf}
        onClick={handleClick}
        offset={yOffset}
        variant={variant !== 'default' && inputValue ? 'relative' : 'absolute'}
        wording={wording}
      />
    );
  });

  if (display === 'list' && activeTree) {
    return (
      <DiagramList
        data={activeTree}
        variant={variant}
        wording={wording}
        display={display}
        setDisplay={setDisplay}
      />
    );
  }

  return (
    <div className="tax-calculator__frame" ref={frameRef} aria-live="polite">
      <div className="tax-calculator__controls">
        {isTablet && <ListSwitch onClick={setDisplay} display={display} />}
        {(activeTree?.data?.parent || activeTree?.parent) && (
          <div className="tax-calculator__back">
            <IconLink
              link={undefined}
              icon="arrow-left-line"
              onClick={() => {
                if (activeTree?.data?.parent || activeTree?.parent)
                  // $FlowFixMe
                  handleClick(activeTree.data.parent || activeTree?.parent);
              }}
              ref={ref}
            >
              {t('Zurück')}
            </IconLink>
          </div>
        )}
      </div>
      <div
        className="tax-calculator__diagram"
        style={{
          height:
            activeTree?.data?.parent || activeTree?.parent
              ? height + rootHeight
              : height,
        }}
      >
        {(activeTree?.data?.parent || activeTree?.parent) && (
          <>
            <RootTile
              rootHeight={rootHeight}
              key={activeTree?.data?.parent?.data.id}
              tile={activeTree}
              onClick={() => {
                if (activeTree?.data?.parent || activeTree?.parent)
                  // $FlowFixMe
                  handleClick(activeTree.data.parent || activeTree?.parent);
              }}
              variant={
                variant !== 'default' && inputValue ? 'relative' : 'absolute'
              }
              wording={wording}
            />
          </>
        )}
        <AnimatePresence initial={false}>{allShapes}</AnimatePresence>
      </div>
    </div>
  );
};
export default Diagram;
